import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@material-ui/core";

const DAYS_IN_MONTH = Array.from({ length: 31 }, (_, i) => i + 1);
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

export type RecurrenceConfig = {
    dayOfMonth: number;
    hour: number;
    minute: number;
};

type Props = {
    open: boolean;
    onClose: () => void;
    onApply: (config: RecurrenceConfig) => void;
    initialConfig?: Partial<RecurrenceConfig>;
    disabled?: boolean;
};

const defaultConfig: RecurrenceConfig = {
    dayOfMonth: 1,
    hour: 0,
    minute: 0,
};

const RecurrencePopup: React.FC<Props> = ({ open, onClose, onApply, initialConfig, disabled }: Props) => {
    const [config, setConfig] = useState<RecurrenceConfig>({ ...defaultConfig, ...initialConfig });

    useEffect(() => {
        if (open) {
            setConfig({ ...defaultConfig, ...initialConfig });
        }
    }, [open, initialConfig]);

    const handleApply = () => {
        onApply(config);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Recurrence Pattern</DialogTitle>
            <DialogContent dividers>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                    Repeat on
                </Typography>
                <Box display="flex" flexDirection="column" style={{ gap: "16px" }}>
                    <FormControl fullWidth>
                        <InputLabel id="day-of-month-label">Day of Month</InputLabel>
                        <Select
                            labelId="day-of-month-label"
                            value={config.dayOfMonth}
                            disabled={disabled}
                            onChange={e => setConfig(prev => ({ ...prev, dayOfMonth: e.target.value as number }))}
                        >
                            {DAYS_IN_MONTH.map(d => (
                                <MenuItem key={d} value={d}>
                                    {d}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box display="flex" style={{ gap: "16px" }}>
                        <FormControl style={{ flex: 1 }}>
                            <InputLabel id="hour-label">Hour</InputLabel>
                            <Select
                                labelId="hour-label"
                                value={config.hour}
                                disabled={disabled}
                                onChange={e => setConfig(prev => ({ ...prev, hour: e.target.value as number }))}
                            >
                                {HOURS.map(h => (
                                    <MenuItem key={h} value={h}>
                                        {String(h).padStart(2, "0")}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl style={{ flex: 1 }}>
                            <InputLabel id="minute-label">Minutes</InputLabel>
                            <Select
                                labelId="minute-label"
                                value={config.minute}
                                disabled={disabled}
                                onChange={e => setConfig(prev => ({ ...prev, minute: e.target.value as number }))}
                            >
                                {MINUTES.map(m => (
                                    <MenuItem key={m} value={m}>
                                        {String(m).padStart(2, "0")}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="default">
                    Cancel
                </Button>
                <Button onClick={handleApply} color="primary" variant="contained" disabled={disabled}>
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RecurrencePopup;
